// One CborSocket per stream page (init-handoff §3.5, §4.6). Reconnect with exponential
// backoff + jitter; heartbeat every 15s; join on open. Keyed strictly by Stream.uuid.
import { getToken } from "../auth/token.js";
import { env } from "../env.js";
import { type ClientMsg, type ServerMsg, decode, encode } from "./protocol.js";

export type ConnState = "connecting" | "connected" | "disconnected";

export interface CborSocketHandlers {
  onMessage: (msg: ServerMsg) => void;
  onState: (state: ConnState) => void;
}

export class CborSocket {
  private ws: WebSocket | null = null;
  private closed = false;
  private retries = 0;
  private hbTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private uuid: string,
    private handlers: CborSocketHandlers,
  ) {}

  connect(): void {
    this.closed = false;
    this.open();
  }

  private open(): void {
    this.handlers.onState("connecting");
    // Cookie-less: pass the session token via query (browsers can't set WS headers). Anonymous
    // viewers omit it and still connect for playback status + counts.
    const token = getToken();
    const url = `${env.wsBase}/stream/${this.uuid}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onopen = () => {
      this.retries = 0;
      this.handlers.onState("connected");
      this.send({ t: "join", streamId: this.uuid });
      this.startHeartbeat();
    };
    ws.onmessage = (ev) => {
      try {
        const msg = decode(new Uint8Array(ev.data as ArrayBuffer)) as ServerMsg;
        this.handlers.onMessage(msg);
      } catch {
        /* ignore malformed */
      }
    };
    ws.onclose = () => {
      this.stopHeartbeat();
      this.handlers.onState("disconnected");
      if (!this.closed) this.scheduleReconnect();
    };
    ws.onerror = () => {
      ws.close();
    };
  }

  private scheduleReconnect(): void {
    this.retries += 1;
    const base = Math.min(1000 * 2 ** this.retries, 15000);
    const jitter = Math.random() * 500;
    this.reconnectTimer = setTimeout(() => this.open(), base + jitter);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.hbTimer = setInterval(() => this.send({ t: "heartbeat" }), 15000);
  }

  private stopHeartbeat(): void {
    if (this.hbTimer) clearInterval(this.hbTimer);
    this.hbTimer = null;
  }

  send(msg: ClientMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(encode(msg));
    }
  }

  close(): void {
    this.closed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
  }
}
